import { MouseEvent } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

// Direct path, not the application barrel: this file is inside it, and importing it from
// itself is a cycle (see CasPendingEmailsPill in CasContextProviders).
import { SEND_AS_OPTIONS, SendAsType } from './composeMode';

/*
  The compose window's title bar also carries its action buttons, so below MD they drop to their
  icons alone. A button keeps its aria-label and its tooltip - a hidden label is not readable by
  assistive tech.
*/
export const LABEL_HIDDEN_BELOW_MD = { display: { xs: 'none', md: 'inline' } };

// Reclaim the space the hidden label would otherwise leave: the button's minimum width and the
// gap the startIcon reserves for text beside it.
export const ICON_ONLY_BELOW_MD = {
  minWidth: { xs: 0, md: 64 },
  '& .MuiButton-startIcon': {
    mr: { xs: 0, md: 1 },
    ml: { xs: 0, md: -0.25 },
  },
};

interface Props {
  value: SendAsType;
  onChange: (sendAs: SendAsType) => void;
}

/*
  "Send As": Auto follows whoever is in To/CC, Email and SMS pin the channel outright.

  It lives in the dialog title, which renders outside the form provider, so it is driven by the
  caller's state rather than by RHF - the caller mirrors the value into the form field, which is
  what everything downstream reads.
*/
export default function SendAsToggle({ value, onChange }: Props) {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      size="small"
      // An exclusive group emits null when the active button is clicked again; keep the current
      // mode rather than leaving the window with none selected.
      onChange={(_: MouseEvent<HTMLElement>, next: SendAsType | null) => next && onChange(next)}
      sx={{ mx: 1 }}
    >
      {SEND_AS_OPTIONS.map(({ id, name }) => (
        <ToggleButton key={id} value={id} sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
          {name}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
